import React, { useEffect } from 'react';
import AppShell from '../../../components/layout/AppShell';
import { Helmet } from 'react-helmet';
import { useUser } from '../../../hooks/useUser';
import {
  DocumentTextIcon,
  AdjustmentsHorizontalIcon,
  PencilSquareIcon,
  ScissorsIcon,
  UserCircleIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/20/solid';

const features = [
  {
    name: 'AI CV Converter',
    description: 'Aligns your old CV to the position description and generates a new, tailored CV.',
    icon: DocumentTextIcon,
  },
  {
    name: 'Customisable Output',
    description: "Personalise your CV's format and content to suit your preferences.",
    icon: AdjustmentsHorizontalIcon,
  },
  {
    name: 'Spelling and Grammar Correction',
    description: 'Automatically corrects any errors to ensure your CV is polished and professional.',
    icon: PencilSquareIcon,
  },
  {
    name: 'Page Count Adjustment',
    description: 'Shortens lengthy CVs to fit a specified page count without losing important details.',
    icon: ScissorsIcon,
  },
  {
    name: 'Perspective Conversion',
    description: 'Converts your CV from 1st person to 3rd person to match industry standards.',
    icon: UserCircleIcon,
  },
  {
    name: 'Multi-job Customisation',
    description: 'Take your existing CV and customise it to match multiple job descriptions effortlessly.',
    icon: ClipboardDocumentListIcon,
  },
];

const Home = () => {
  const { user } = useUser();
  const isLoggedIn = Boolean(user);

  useEffect(() => {
    const video = document.getElementById('demoVideo');
    const playButton = document.getElementById('playButton');
    const watchAgainButton = document.getElementById('watchAgainButton');
    const tryNowButton = document.getElementById('tryNowButton');
    const overlay = document.getElementById('overlay');

    if (!video || !playButton || !watchAgainButton || !tryNowButton || !overlay) {
      return undefined;
    }

    const showOverlay = () => {
      overlay.classList.remove('hidden');
      playButton.style.display = 'flex';
      watchAgainButton.style.display = 'none';
      tryNowButton.style.display = 'none';
    };

    const showEndOverlay = () => {
      overlay.classList.remove('hidden');
      playButton.style.display = 'none';
      watchAgainButton.style.display = 'flex';
      tryNowButton.style.display = 'flex';
    };

    const hideOverlay = () => {
      overlay.classList.add('hidden');
      playButton.style.display = 'none';
    };

    const handlePlay = () => hideOverlay();
    const handlePause = () => showOverlay();
    const handleEnded = () => showEndOverlay();
    const handlePlayButtonClick = () => {
      video.play();
      hideOverlay();
    };
    const handleWatchAgainClick = () => {
      video.currentTime = 0;
      video.play();
      hideOverlay();
    };
    const handleTryNowClick = () => {
      window.location.href = '/register';
    };

    showOverlay();

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    playButton.addEventListener('click', handlePlayButtonClick);
    watchAgainButton.addEventListener('click', handleWatchAgainClick);
    tryNowButton.addEventListener('click', handleTryNowClick);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      playButton.removeEventListener('click', handlePlayButtonClick);
      watchAgainButton.removeEventListener('click', handleWatchAgainClick);
      tryNowButton.removeEventListener('click', handleTryNowClick);
    };
  }, []);

  return (
    <AppShell currentPage="Home" loginRequired={false}>
      <div className="public-page">
        <Helmet>
          <title>CVHQ | CV Converter | AI-Powered Resume Conversion & Tailoring</title>
          <meta
            name="description"
            content="Revolutionise your job search with our free AI-powered CV Converter. Convert, personalise, and correct your resume in less than 30 seconds. Tailor your CV to specific job descriptions and create a professional CV from plain text effortlessly."
          />
          <meta
            name="keywords"
            content="CV converter, resume converter, free CV converter, free resume converter, AI-powered CV converter, AI-driven resume conversion, convert CV online, convert resume online, tailor CV, tailor resume, customize CV, customize resume, optimize CV, optimize resume, professional CV, professional resume, CV builder, resume builder, CV template, resume template, job application, career boost, increase job prospects, land dream job"
          />
        </Helmet>
        <div className="public-page-inner">
          <div className="space-y-16">
            <section className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
              <div className="space-y-8">
                <div className="space-y-4">
                  <h1 className="text-4xl font-bold tracking-tight text-darkGrey sm:text-5xl lg:text-6xl">
                    Focus your CV for Unlimited Opportunities
                  </h1>
                  <p className="max-w-2xl text-lg leading-8 text-slateGrey">
                    Transform your CV for every opportunity. CVHQ empowers you to craft tailored, professional resumes that align perfectly with each role's unique requirements. Say goodbye to generic applications and hello to standout submissions that showcase your true potential.
                  </p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  {!isLoggedIn && (
                    <a href="/register" className="public-primary-button">
                      Register Now
                    </a>
                  )}
                  <a href="/How-It-Works" className="public-secondary-button">
                    How It Works
                  </a>
                  <a href="/Scenarios" className="public-secondary-button">
                    Explore Scenarios
                  </a>
                </div>
              </div>

              <div className="application-panel relative overflow-hidden">
                <video
                  id="demoVideo"
                  src="/CVHQ Demo Video.mp4"
                  className="h-full w-full object-cover"
                  muted
                  playsInline
                  poster="/CVHQDemoVideoPoster.png"
                />
                <div
                  id="overlay"
                  className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/45 transition-opacity duration-300"
                >
                  <button
                    id="playButton"
                    className="public-secondary-button border-white bg-white text-darkGrey hover:border-white hover:bg-slate-50"
                  >
                    Discover the 3-Step CV Conversion Process
                  </button>
                  <button
                    id="watchAgainButton"
                    className="public-secondary-button hidden mt-4 border-white bg-white text-darkGrey hover:border-white hover:bg-slate-50"
                  >
                    Watch Again
                  </button>
                  <button id="tryNowButton" className="public-primary-button hidden mt-4">
                    Try Now
                  </button>
                </div>
              </div>
            </section>

            <section className="space-y-8">
              <div className="max-w-3xl">
                <h2 className="text-2xl font-bold tracking-tight text-darkGrey sm:text-3xl">
                  Everything you need to tailor each application
                </h2>
                <p className="mt-3 text-lg leading-8 text-slateGrey">
                  Keep your process simple: convert, personalise, and refine your CV in one flow.
                </p>
              </div>
              <dl className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {features.map((feature) => (
                  <div key={feature.name} className="public-card public-card-body h-full">
                    <div className="flex items-start gap-4">
                      <feature.icon className="public-icon mt-1" aria-hidden="true" />
                      <div>
                        <dt className="font-semibold text-darkGrey">{feature.name}</dt>
                        <dd className="mt-1 text-sm leading-6 text-slateGrey">{feature.description}</dd>
                      </div>
                    </div>
                  </div>
                ))}
              </dl>
            </section>

            <div className="flex items-center justify-center">
              <a href="/About" className="public-primary-button">
                Learn more
              </a>
            </div>

            <section className="public-card-muted p-6 sm:p-8 lg:p-10">
              <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
                <div className="space-y-6">
                  <div className="text-center lg:text-left">
                    <h2 className="text-2xl font-bold tracking-tight text-darkGrey sm:text-3xl">
                      Customised CV Layouts for Corporate Clients
                    </h2>
                    <p className="mt-4 text-lg leading-8 text-slateGrey">
                      Elevate your company's recruitment process with bespoke CV layouts. We offer tailored solutions that align perfectly with your corporate branding and specific requirements.
                    </p>
                  </div>
                  <div className="flex flex-col gap-4 sm:flex-row sm:justify-center lg:justify-start">
                    <a href="mailto:Support@CVHQ.com.au" className="public-primary-button">
                      Contact Us for Corporate Solutions
                    </a>
                    <a href="/Scenarios" className="public-secondary-button">
                      View Use Cases
                    </a>
                  </div>
                  <p className="text-center text-xs text-slateGrey lg:text-left">Support@CVHQ.com.au</p>
                </div>

                <div className="public-card public-card-body">
                  <h3 className="mb-6 text-center text-xl font-semibold tracking-tight text-darkGrey">
                    Why Choose Our Corporate Solutions?
                  </h3>
                  <ul className="space-y-4 text-slateGrey">
                    <li className="flex items-start gap-3">
                      <svg className="public-icon mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Customized templates that reflect your brand identity</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="public-icon mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Streamlined recruitment process with standardized CV formats</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="public-icon mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Enhanced candidate experience with professional, branded CVs</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <svg className="public-icon mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Dedicated support for seamless integration and ongoing assistance</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 border-t border-slate-200 pt-8 text-center">
                <p className="text-base font-semibold text-primaryBlue sm:text-lg">
                  Contact us to have your Corporate CV converted into a customisable template for use with CVHQ. This will allow you to easily format CVs according to your company's requirements.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default Home;


